import axios from 'axios';
import EventBus from '@/utils/eventBus';
import { ApiBaseUrl } from '@/constants';
import apiController from '@/utils/apiController';

/**
 * Shared bulk persist for line ↔ cercas (vigência/ordem) ↔ rotas ↔ turnos.
 * Host component must provide: token, editedIndex, cercasLinha, rotasLinha, turnosPassageirosLinha,
 * loadingInsertCercas/Rotas/Sublinhas/TurnosPassageiros, operacao, updateTable(status).
 * When assistantUpdatingExistingLinha, cercasLinha entries may set needsNewLinhaCercaBulkLink: false
 * if cercas_linhas already has that (id_cerca, id_tipo_cerca, id_sentido) for the line — then bulk insert is skipped for them but vigência/ordem is still refreshed for the full list.
 */
export default {
  methods: {
    /**
     * Agenda comparação km informado × Verso (histórico CALCULADA/INFORMADA) em background.
     * Delay curto para cercas/rotas da cascata persistirem.
     */
    scheduleCompararDistanciaInformada(idLinha, delayMs = 500) {
      const id = idLinha != null ? Number(idLinha) : null;
      if (!Number.isFinite(id) || id <= 0) return;
      const wait = Number.isFinite(Number(delayMs)) ? Math.max(0, Number(delayMs)) : 500;
      setTimeout(() => {
        apiController.request(apiController.endpoints.compararDistanciainformadalinha, {
          data: { id_linha: id, origem: 'CRUD_LINHAS', async: true },
          silent: true,
        });
      }, wait);
    },
    normalizeCercasOrdemParada(cercasArray) {
      if (!cercasArray || cercasArray.length === 0) {
        return [];
      }
      const normalizedCercas = cercasArray.map((cerca) => ({ ...cerca }));
      let maxOrdem = 0;
      for (let i = 0; i < normalizedCercas.length; i++) {
        const cerca = normalizedCercas[i];
        if (!cerca) continue;
        const ordem = cerca.ordem_parada;
        if (ordem != null && ordem !== undefined && ordem !== 0 && ordem !== '' && !isNaN(ordem) && parseInt(ordem) > 0) {
          maxOrdem = Math.max(maxOrdem, parseInt(ordem));
        }
      }
      for (let i = 0; i < normalizedCercas.length; i++) {
        const cerca = normalizedCercas[i];
        if (!cerca) continue;
        const ordem = cerca.ordem_parada;
        if (ordem == null || ordem === undefined || ordem === 0 || ordem === '' || isNaN(ordem) || parseInt(ordem) < 1) {
          maxOrdem++;
          cerca.ordem_parada = maxOrdem;
        } else {
          cerca.ordem_parada = parseInt(ordem);
        }
      }
      normalizedCercas.sort((a, b) => {
        const ordemA = a.ordem_parada || 0;
        const ordemB = b.ordem_parada || 0;
        return ordemA - ordemB;
      });
      return normalizedCercas;
    },
    insertCercasBulk(idlinha) {
      this.loadingInsertCercas = true;
      const normalizedCercas = this.normalizeCercasOrdemParada(this.cercasLinha || []);
      this.cercasLinha = normalizedCercas;
      const cercasLinhaCopy = JSON.parse(JSON.stringify(normalizedCercas));
      const bulkRows = this.cercasLinhaEntriesForBulkInsert();
      const temp = [];
      for (let i = 0; i < bulkRows.length; i++) {
        const cerca = bulkRows[i];
        if (!cerca) continue;
        const ordemParada =
          cerca.ordem_parada != null && cerca.ordem_parada > 0 ? parseInt(cerca.ordem_parada) : i + 1;
        temp.push([
          idlinha,
          cerca.id_cerca,
          cerca.id_tipo_cerca,
          cerca.show_sinotico,
          cerca.tempo_chegada_minutos,
          cerca.id_sentido,
          ordemParada,
        ]);
      }
      const continueAfterBulk = (response) => {
        if (cercasLinhaCopy.length > 0) {
          const newLineCercasPath = this.editedIndex === -1 && !this.assistantUpdatingExistingLinha;
          if (newLineCercasPath) {
            this.insertCercasVigencia(idlinha, cercasLinhaCopy);
          } else {
            this.updateCercasVigenciaOrdem(idlinha, cercasLinhaCopy);
          }
        } else {
          this.loadingInsertCercas = false;
          this.updateTable(response && response.data ? response.data.erro : false);
        }
      };
      if (temp.length === 0) {
        continueAfterBulk(null);
        return;
      }
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertcercaslinhabulk', temp, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          continueAfterBulk(response);
        })
        .catch((e) => {
          this.loadingInsertCercas = false;
          console.log('ERRO AXIOS');
          console.log(e);
          EventBus.$emit('show-error', { error: e });
        });
    },
    insertCercasVigencia(idlinha, cercasLinhaCopy) {
      const vigenciaData = {
        vigencia: {
          id_linha: idlinha,
          data_inicio: null,
          hora_inicio: null,
          data_fim: null,
          hora_fim: null,
          padrao: 1,
          ativa: 1,
        },
      };
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertcercavigenciacomsequencia', vigenciaData, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          if (response.data.erro) {
            console.error('Erro ao inserir vigência:', response.data.mensagem);
            this.loadingInsertCercas = false;
            this.updateTable(response.data.erro);
          } else {
            const id_associacao = response.data.id_associacao;
            this.insertCercasOrdem(id_associacao, cercasLinhaCopy);
          }
        })
        .catch((e) => {
          console.log('ERRO AXIOS ao inserir vigência');
          console.log(e.message);
          this.loadingInsertCercas = false;
          EventBus.$emit('show-error', { error: e });
          this.updateTable(true);
        });
    },
    insertCercasOrdem(id_associacao, cercasLinhaCopy) {
      const cercasToProcess = cercasLinhaCopy || this.cercasLinha || [];
      if (!cercasToProcess || cercasToProcess.length === 0) {
        console.warn('insertCercasOrdem: cercasToProcess is empty');
        this.loadingInsertCercas = false;
        this.updateTable(false);
        return;
      }
      const normalizedCercas = this.normalizeCercasOrdemParada(cercasToProcess);
      const cercasOrdem = [];
      for (let i = 0; i < normalizedCercas.length; i++) {
        const cerca = normalizedCercas[i];
        if (!cerca) continue;
        let ordemParada = cerca.ordem_parada;
        if (ordemParada == null || ordemParada === undefined || ordemParada === 0 || ordemParada === '' || isNaN(ordemParada) || ordemParada < 1) {
          ordemParada = i + 1;
        } else {
          ordemParada = parseInt(ordemParada);
        }
        if (!id_associacao || !cerca.id_cerca) {
          console.error(`Error: Missing required fields for cerca. id_associacao: ${id_associacao}, id_cerca: ${cerca.id_cerca}`);
          continue;
        }
        cercasOrdem.push([id_associacao, cerca.id_cerca, ordemParada, cerca.id_sentido || null, cerca.id_tipo_cerca || null]);
      }
      if (cercasOrdem.length === 0) {
        console.warn('insertCercasOrdem: No valid cercas to insert after validation');
        this.loadingInsertCercas = false;
        this.updateTable(false);
        return;
      }
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertcercaordembulk', cercasOrdem, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          this.loadingInsertCercas = false;
          if (response.data.erro) {
            console.error('Erro ao inserir cercas ordem:', response.data.mensagem);
            EventBus.$emit('show-error', { error: { message: response.data.mensagem } });
          }
          this.updateTable(response.data.erro);
        })
        .catch((e) => {
          console.error('ERRO AXIOS ao inserir cercas ordem:', e);
          this.loadingInsertCercas = false;
          EventBus.$emit('show-error', { error: e });
          this.updateTable(true);
        });
    },
    updateCercasVigenciaOrdem(idlinha, cercasLinhaCopy) {
      axios
        .post(ApiBaseUrl + '/api/v1.0/getcercaspadraoporlinha/', [idlinha], {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          const padraoCercas = response.data || [];
          if (padraoCercas.length > 0) {
            const id_associacao = padraoCercas[0].id_associacao;
            if (id_associacao) {
              axios
                .delete(ApiBaseUrl + '/api/v1.0/deletecercaordem/' + id_associacao, {
                  headers: { 'x-access-token': this.token },
                })
                .then(() => {
                  this.insertCercasOrdem(id_associacao, cercasLinhaCopy);
                })
                .catch((e) => {
                  console.log('ERRO AXIOS ao deletar cercas ordem');
                  console.log(e.message);
                  this.loadingInsertCercas = false;
                  EventBus.$emit('show-error', { error: e });
                  this.updateTable(true);
                });
            } else {
              this.insertCercasVigencia(idlinha, cercasLinhaCopy);
            }
          } else {
            this.insertCercasVigencia(idlinha, cercasLinhaCopy);
          }
        })
        .catch((e) => {
          console.log('ERRO AXIOS ao buscar padrão vigência');
          console.log(e);
          EventBus.$emit('show-error', { error: e });
          this.insertCercasVigencia(idlinha, cercasLinhaCopy);
        });
    },
    rotasLinhaEntriesForBulkInsert() {
      const all = this.rotasLinha || [];
      if (!this.assistantUpdatingExistingLinha) return all;
      return all.filter((r) => r && r.needsNewLinhaRotaBulkLink);
    },
    rotasLinhaNeedsBulkInsert() {
      return this.rotasLinhaEntriesForBulkInsert().length > 0;
    },
    cercasLinhaEntriesForBulkInsert() {
      const all = this.cercasLinha || [];
      if (!this.assistantUpdatingExistingLinha) return all;
      return all.filter((c) => c && c.needsNewLinhaCercaBulkLink !== false);
    },
    insertRotasBulk(idlinha) {
      this.loadingInsertRotas = true;
      let rows = this.rotasLinhaEntriesForBulkInsert();
      if (!rows.length) {
        this.loadingInsertRotas = false;
        return;
      }
      const temp = [];
      for (let i = 0; i < rows.length; i++) {
        temp.push([idlinha, rows[i].id_rota, rows[i].id_tipo_rota, rows[i].id_sentido, 1]);
      }
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertrotaslinhabulk', temp, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          this.loadingInsertRotas = false;
          this.updateTable(response.data.erro);
        })
        .catch((e) => {
          this.loadingInsertRotas = false;
          console.log('ERRO AXIOS');
          console.log(e);
          EventBus.$emit('show-error', { error: e });
        });
    },
    insertTurnosPassageirosBulk(idlinha, turnosPassageirosLinhaCopy) {
      this.loadingInsertTurnosPassageiros = true;
      const turnosToProcess = turnosPassageirosLinhaCopy || this.turnosPassageirosLinha || [];
      const temp = turnosToProcess.map((t) => [idlinha, t.id_turno, t.lotacao]);
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertturnoslinhabulk', temp, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          this.loadingInsertTurnosPassageiros = false;
          this.updateTable(response.data.erro);
        })
        .catch((e) => {
          this.loadingInsertTurnosPassageiros = false;
          console.log('ERRO AXIOS');
          console.log(e);
          EventBus.$emit('show-error', { error: e });
        });
    },
    /**
     * Same as insertRotasBulk but resolves/rejects a Promise instead of calling updateTable.
     * Used by LinhaAssistenteDialog for sequential multi-line KML saves.
     */
    insertRotasBulkAsync(idlinha) {
      return new Promise((resolve, reject) => {
        let rows = this.rotasLinhaEntriesForBulkInsert();
        if (!rows.length) {
          resolve();
          return;
        }
        this.loadingInsertRotas = true;
        const temp = [];
        for (let i = 0; i < rows.length; i++) {
          temp.push([idlinha, rows[i].id_rota, rows[i].id_tipo_rota, rows[i].id_sentido, 1]);
        }
        axios
          .post(ApiBaseUrl + '/api/v1.0/insertrotaslinhabulk', temp, {
            headers: { 'x-access-token': this.token },
          })
          .then((response) => {
            this.loadingInsertRotas = false;
            if (response.data.erro) {
              reject(new Error(response.data.mensagem || 'insertrotaslinhabulk'));
            } else {
              resolve();
            }
          })
          .catch((e) => {
            this.loadingInsertRotas = false;
            console.log('ERRO AXIOS');
            console.log(e);
            EventBus.$emit('show-error', { error: e });
            reject(e);
          });
      });
    },
    insertTurnosPassageirosBulkAsync(idlinha, turnosPassageirosLinhaCopy) {
      return new Promise((resolve, reject) => {
        this.loadingInsertTurnosPassageiros = true;
        const turnosToProcess = turnosPassageirosLinhaCopy || this.turnosPassageirosLinha || [];
        const temp = turnosToProcess.map((t) => [idlinha, t.id_turno, t.lotacao]);
        axios
          .post(ApiBaseUrl + '/api/v1.0/insertturnoslinhabulk', temp, {
            headers: { 'x-access-token': this.token },
          })
          .then((response) => {
            this.loadingInsertTurnosPassageiros = false;
            if (response.data.erro) {
              reject(new Error(response.data.mensagem || 'insertturnoslinhabulk'));
            } else {
              resolve();
            }
          })
          .catch((e) => {
            this.loadingInsertTurnosPassageiros = false;
            console.log('ERRO AXIOS');
            console.log(e);
            EventBus.$emit('show-error', { error: e });
            reject(e);
          });
      });
    },
    /**
     * Full cercas/vigência/ordem chain without updateTable; resolves when done.
     */
    insertCercasBulkAsync(idlinha) {
      return new Promise((resolve, reject) => {
        this.loadingInsertCercas = true;
        const normalizedCercas = this.normalizeCercasOrdemParada(this.cercasLinha || []);
        this.cercasLinha = normalizedCercas;
        const cercasLinhaCopy = JSON.parse(JSON.stringify(normalizedCercas));
        const bulkRows = this.cercasLinhaEntriesForBulkInsert();
        const temp = [];
        for (let i = 0; i < bulkRows.length; i++) {
          const cerca = bulkRows[i];
          if (!cerca) continue;
          const ordemParada =
            cerca.ordem_parada != null && cerca.ordem_parada > 0 ? parseInt(cerca.ordem_parada) : i + 1;
          temp.push([
            idlinha,
            cerca.id_cerca,
            cerca.id_tipo_cerca,
            cerca.show_sinotico,
            cerca.tempo_chegada_minutos,
            cerca.id_sentido,
            ordemParada,
          ]);
        }
        const continueAfterBulkAsync = () => {
          if (cercasLinhaCopy.length > 0) {
            const newLineCercasPath = this.editedIndex === -1 && !this.assistantUpdatingExistingLinha;
            if (newLineCercasPath) {
              this._insertCercasVigenciaAsync(idlinha, cercasLinhaCopy, resolve, reject);
            } else {
              this._updateCercasVigenciaOrdemAsync(idlinha, cercasLinhaCopy, resolve, reject);
            }
          } else {
            this.loadingInsertCercas = false;
            resolve();
          }
        };
        if (temp.length === 0) {
          continueAfterBulkAsync();
          return;
        }
        axios
          .post(ApiBaseUrl + '/api/v1.0/insertcercaslinhabulk', temp, {
            headers: { 'x-access-token': this.token },
          })
          .then(() => {
            continueAfterBulkAsync();
          })
          .catch((e) => {
            this.loadingInsertCercas = false;
            console.log('ERRO AXIOS');
            console.log(e);
            EventBus.$emit('show-error', { error: e });
            reject(e);
          });
      });
    },
    _insertCercasVigenciaAsync(idlinha, cercasLinhaCopy, resolve, reject) {
      const vigenciaData = {
        vigencia: {
          id_linha: idlinha,
          data_inicio: null,
          hora_inicio: null,
          data_fim: null,
          hora_fim: null,
          padrao: 1,
          ativa: 1,
        },
      };
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertcercavigenciacomsequencia', vigenciaData, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          if (response.data.erro) {
            console.error('Erro ao inserir vigência:', response.data.mensagem);
            this.loadingInsertCercas = false;
            reject(new Error(response.data.mensagem || 'vigencia'));
          } else {
            const id_associacao = response.data.id_associacao;
            this._insertCercasOrdemAsync(id_associacao, cercasLinhaCopy, resolve, reject);
          }
        })
        .catch((e) => {
          console.log('ERRO AXIOS ao inserir vigência');
          console.log(e.message);
          this.loadingInsertCercas = false;
          EventBus.$emit('show-error', { error: e });
          reject(e);
        });
    },
    _insertCercasOrdemAsync(id_associacao, cercasLinhaCopy, resolve, reject) {
      const cercasToProcess = cercasLinhaCopy || this.cercasLinha || [];
      if (!cercasToProcess || cercasToProcess.length === 0) {
        console.warn('insertCercasOrdem: cercasToProcess is empty');
        this.loadingInsertCercas = false;
        resolve();
        return;
      }
      const normalizedCercas = this.normalizeCercasOrdemParada(cercasToProcess);
      const cercasOrdem = [];
      for (let i = 0; i < normalizedCercas.length; i++) {
        const cerca = normalizedCercas[i];
        if (!cerca) continue;
        let ordemParada = cerca.ordem_parada;
        if (ordemParada == null || ordemParada === undefined || ordemParada === 0 || ordemParada === '' || isNaN(ordemParada) || ordemParada < 1) {
          ordemParada = i + 1;
        } else {
          ordemParada = parseInt(ordemParada);
        }
        if (!id_associacao || !cerca.id_cerca) {
          console.error(`Error: Missing required fields for cerca. id_associacao: ${id_associacao}, id_cerca: ${cerca.id_cerca}`);
          continue;
        }
        cercasOrdem.push([id_associacao, cerca.id_cerca, ordemParada, cerca.id_sentido || null, cerca.id_tipo_cerca || null]);
      }
      if (cercasOrdem.length === 0) {
        console.warn('insertCercasOrdem: No valid cercas to insert after validation');
        this.loadingInsertCercas = false;
        resolve();
        return;
      }
      axios
        .post(ApiBaseUrl + '/api/v1.0/insertcercaordembulk', cercasOrdem, {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          this.loadingInsertCercas = false;
          if (response.data.erro) {
            console.error('Erro ao inserir cercas ordem:', response.data.mensagem);
            EventBus.$emit('show-error', { error: { message: response.data.mensagem } });
            reject(new Error(response.data.mensagem || 'cercaordem'));
          } else {
            resolve();
          }
        })
        .catch((e) => {
          console.error('ERRO AXIOS ao inserir cercas ordem:', e);
          this.loadingInsertCercas = false;
          EventBus.$emit('show-error', { error: e });
          reject(e);
        });
    },
    _updateCercasVigenciaOrdemAsync(idlinha, cercasLinhaCopy, resolve, reject) {
      axios
        .post(ApiBaseUrl + '/api/v1.0/getcercaspadraoporlinha/', [idlinha], {
          headers: { 'x-access-token': this.token },
        })
        .then((response) => {
          const padraoCercas = response.data || [];
          if (padraoCercas.length > 0) {
            const id_associacao = padraoCercas[0].id_associacao;
            if (id_associacao) {
              axios
                .delete(ApiBaseUrl + '/api/v1.0/deletecercaordem/' + id_associacao, {
                  headers: { 'x-access-token': this.token },
                })
                .then(() => {
                  this._insertCercasOrdemAsync(id_associacao, cercasLinhaCopy, resolve, reject);
                })
                .catch((e) => {
                  console.log('ERRO AXIOS ao deletar cercas ordem');
                  console.log(e.message);
                  this.loadingInsertCercas = false;
                  EventBus.$emit('show-error', { error: e });
                  reject(e);
                });
            } else {
              this._insertCercasVigenciaAsync(idlinha, cercasLinhaCopy, resolve, reject);
            }
          } else {
            this._insertCercasVigenciaAsync(idlinha, cercasLinhaCopy, resolve, reject);
          }
        })
        .catch((e) => {
          console.log('ERRO AXIOS ao buscar padrão vigência');
          console.log(e);
          EventBus.$emit('show-error', { error: e });
          this._insertCercasVigenciaAsync(idlinha, cercasLinhaCopy, resolve, reject);
        });
    },
  },
};
